import { Layout } from '@/components/Layout';
import { Link } from 'react-router-dom';

export function PrivacyPage() {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-12 px-4">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-gray-600 mb-8">Last updated: January 21, 2026</p>

        <div className="prose prose-blue max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              Vistora, Inc. ("we", "us", or "our") operates vistora.app (the "Service"). This Privacy Policy informs
              you of our policies regarding the collection, use, and disclosure of personal data when you use our
              Service and the choices you have associated with that data.
            </p>
            <p className="text-gray-700 mb-4">
              We use your data to provide and improve the Service. By using the Service, you agree to the collection
              and use of information in accordance with this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.1 Personal Information</h3>
            <p className="text-gray-700 mb-4">
              When you create an account, we collect:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>Email address</strong> - Used for authentication and communication</li>
              <li><strong>Full name</strong> - Used for personalization and display</li>
              <li><strong>Password</strong> - Stored as a cryptographic hash (PBKDF2-SHA256), never in plaintext</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.2 Usage Data</h3>
            <p className="text-gray-700 mb-4">
              We automatically collect certain information when you use the Service:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>Login activity</strong> - Timestamps of when you access the Service</li>
              <li><strong>IP address</strong> - Used for security, rate limiting, and troubleshooting</li>
              <li><strong>Request metadata</strong> - HTTP methods, paths, response codes, and request duration</li>
              <li><strong>Error logs</strong> - Technical information when errors occur to help us improve the Service</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.3 Content Data</h3>
            <p className="text-gray-700 mb-4">
              The Service stores content you create:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>Statements of Work (SOWs)</strong> - Including titles, descriptions, and owner information</li>
              <li><strong>Change history</strong> - All modifications made to your SOWs with timestamps and descriptions</li>
              <li><strong>Client access tokens</strong> - Secure tokens you generate to share SOWs with clients</li>
              <li><strong>Acknowledgment records</strong> - When clients acknowledge changes to SOWs</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.4 Subscription and Billing Data</h3>
            <p className="text-gray-700 mb-4">
              If you subscribe to a paid plan, we collect:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>Subscription tier</strong> - Which plan you're subscribed to (Free, Pro, or Enterprise)</li>
              <li><strong>Subscription status</strong> - Whether your subscription is active, past due, canceled, or in trial</li>
              <li><strong>Billing period dates</strong> - When your subscription renews</li>
              <li><strong>Stripe customer ID</strong> - Reference to your Stripe account (payment processing is handled by Stripe)</li>
            </ul>
            <p className="text-gray-700 mb-4">
              <strong>Important:</strong> We do not store your credit card information. All payment processing is securely
              handled by Stripe, Inc., our payment processor.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">
              We use the collected data for various purposes:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li>To provide and maintain the Service</li>
              <li>To authenticate your identity and manage your account</li>
              <li>To notify you about changes to the Service or your account</li>
              <li>To send you welcome emails and password reset links</li>
              <li>To manage your subscription and billing</li>
              <li>To monitor usage and detect abuse or security incidents</li>
              <li>To provide customer support</li>
              <li>To analyze and improve the Service</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Storage and Security</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.1 Data Storage</h3>
            <p className="text-gray-700 mb-4">
              Your data is stored on infrastructure provided by Cloudflare, Inc., a leading cloud platform provider.
              Our servers are located in the United States.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.2 Security Measures</h3>
            <p className="text-gray-700 mb-4">
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>Encryption in transit</strong> - All data transmitted between your device and our servers is encrypted using HTTPS/TLS</li>
              <li><strong>Password hashing</strong> - Passwords are hashed using PBKDF2-SHA256 before storage</li>
              <li><strong>Access control</strong> - You can only access SOWs you own or have been granted access to</li>
              <li><strong>Rate limiting</strong> - Protection against brute force attacks (100 requests per minute per IP)</li>
              <li><strong>Secure tokens</strong> - JWT authentication and unguessable client access tokens (UUIDs)</li>
            </ul>
            <p className="text-gray-700 mb-4">
              While we strive to protect your personal information, no method of transmission over the Internet or
              electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.3 Data Retention</h3>
            <p className="text-gray-700 mb-4">
              We retain your personal information for as long as your account is active or as needed to provide you
              services. Specific retention policies:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>Account data</strong> - Retained until you delete your account</li>
              <li><strong>SOWs and change history</strong> - Retained until you delete them or close your account</li>
              <li><strong>Password reset tokens</strong> - Automatically expire after 1 hour</li>
              <li><strong>Application logs</strong> - Retained according to our logging provider's retention policy (Axiom)</li>
              <li><strong>Rate limit data</strong> - Stored temporarily in memory, reset on server restart</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Third-Party Services</h2>
            <p className="text-gray-700 mb-4">
              We use the following third-party service providers to operate our Service:
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.1 Stripe (Payment Processing)</h3>
            <p className="text-gray-700 mb-4">
              Stripe, Inc. processes all subscription payments. When you subscribe to a paid plan, you are redirected
              to Stripe's secure checkout page. Stripe collects and processes your payment information according to
              their privacy policy:{' '}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                https://stripe.com/privacy
              </a>
            </p>
            <p className="text-gray-700 mb-4">
              We receive limited information from Stripe (subscription status, customer ID, billing periods) but never
              receive your full payment card details.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.2 Resend (Email Delivery)</h3>
            <p className="text-gray-700 mb-4">
              Resend, Inc. delivers transactional emails on our behalf (welcome emails and password reset emails).
              They receive your email address and name for delivery purposes only. Resend's privacy policy:{' '}
              <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                https://resend.com/legal/privacy-policy
              </a>
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.3 Axiom (Application Logging)</h3>
            <p className="text-gray-700 mb-4">
              Axiom, Inc. stores application logs for performance monitoring and troubleshooting. Logs may contain
              request metadata, error messages, and anonymized user IDs. Axiom's privacy policy:{' '}
              <a href="https://axiom.co/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                https://axiom.co/privacy
              </a>
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.4 Cloudflare (Hosting Infrastructure)</h3>
            <p className="text-gray-700 mb-4">
              Cloudflare, Inc. provides our hosting infrastructure and content delivery network. Your data passes through
              Cloudflare's network when you use the Service. Cloudflare's privacy policy:{' '}
              <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                https://www.cloudflare.com/privacypolicy/
              </a>
            </p>

            <p className="text-gray-700 mb-4 mt-6">
              <strong>We do not sell your data to third parties.</strong> We only share data with service providers
              necessary to operate the Service, and they are contractually obligated to protect your data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Cookies and Tracking</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">6.1 Authentication Tokens</h3>
            <p className="text-gray-700 mb-4">
              We use browser local storage (not cookies) to store your authentication token (JWT). This token is required
              to keep you logged in and authenticate your API requests. The token is stored locally on your device and
              is not accessible to other websites.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">6.2 Analytics</h3>
            <p className="text-gray-700 mb-4">
              <strong>We do not use third-party analytics services</strong> such as Google Analytics. We only collect
              application performance data through our logging provider (Axiom) for troubleshooting and improving the
              Service.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">6.3 Do Not Track</h3>
            <p className="text-gray-700 mb-4">
              We do not track users across third-party websites. However, we do not currently respond to "Do Not Track"
              browser signals as there is no industry standard for compliance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Data Protection Rights (GDPR)</h2>
            <p className="text-gray-700 mb-4">
              If you are a resident of the European Economic Area (EEA), you have certain data protection rights under
              the General Data Protection Regulation (GDPR):
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">7.1 Right to Access</h3>
            <p className="text-gray-700 mb-4">
              You have the right to request copies of your personal data. You can export all your data (profile, SOWs,
              and subscription information) in JSON format through the Settings page under "Data & Privacy". Alternatively,
              you can contact us at{' '}
              <a href="mailto:privacy@vistora.app" className="text-blue-600 hover:underline">privacy@vistora.app</a>.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">7.2 Right to Rectification</h3>
            <p className="text-gray-700 mb-4">
              You have the right to request correction of inaccurate or incomplete data. You can update your name and
              email address in your account settings.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">7.3 Right to Erasure</h3>
            <p className="text-gray-700 mb-4">
              You have the right to request deletion of your personal data. You can delete your account through the
              Settings page under "Data & Privacy". Alternatively, you can contact us at{' '}
              <a href="mailto:privacy@vistora.app" className="text-blue-600 hover:underline">privacy@vistora.app</a>.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Note:</strong> If you delete your account, your login credentials will be removed immediately.
              However, your personal information (name, email) and Statements of Work will be retained for legal and
              contractual purposes as described in our Data Retention section below. Your clients will continue to have
              access to SOWs you shared with them via their access links.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">7.4 Right to Restrict Processing</h3>
            <p className="text-gray-700 mb-4">
              You have the right to request that we restrict the processing of your personal data under certain conditions.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">7.5 Right to Data Portability</h3>
            <p className="text-gray-700 mb-4">
              You have the right to request transfer of your data to another organization or directly to you in a
              structured, commonly used, machine-readable format. You can export all your data (profile, SOWs, and
              subscription information) as a JSON file through the Settings page under "Data & Privacy".
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">7.6 Right to Object</h3>
            <p className="text-gray-700 mb-4">
              You have the right to object to our processing of your personal data under certain conditions.
            </p>

            <p className="text-gray-700 mb-4 mt-6">
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:privacy@vistora.app" className="text-blue-600 hover:underline">privacy@vistora.app</a>.
              We will respond to your request within 30 days.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Data Retention</h2>
            <p className="text-gray-700 mb-4">
              We retain your personal data as follows:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>Active accounts:</strong> Data is retained as long as your account remains active</li>
              <li><strong>Deleted accounts:</strong> If you delete your account, your login credentials are removed,
              but your personal information (name, email) and Statements of Work are retained for 7 years</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">8.1 Purpose of Retention</h3>
            <p className="text-gray-700 mb-4">
              We retain data from deleted accounts to:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li>Comply with legal obligations and regulatory requirements</li>
              <li>Enable the establishment, exercise, or defense of legal claims</li>
              <li>Protect the rights of your clients who rely on SOWs you created</li>
              <li>Maintain business records for tax and accounting purposes</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">8.2 Legal Basis</h3>
            <p className="text-gray-700 mb-4">
              Under GDPR Article 17(3)(b), the "right to erasure" does not apply when data processing is necessary
              for the establishment, exercise, or defense of legal claims. Statements of Work are contractual documents
              between you and your clients, creating legitimate grounds for data retention.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">8.3 After Retention Period</h3>
            <p className="text-gray-700 mb-4">
              After the 7-year retention period expires, we will anonymize or delete your data unless:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li>We are required by law to retain it longer</li>
              <li>There is an active legal claim involving your data</li>
              <li>Your clients have requested continued access to SOWs</li>
            </ul>

            <p className="text-gray-700 mb-4 mt-6">
              If you have questions about our data retention practices, contact{' '}
              <a href="mailto:privacy@vistora.app" className="text-blue-600 hover:underline">privacy@vistora.app</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. International Data Transfers</h2>
            <p className="text-gray-700 mb-4">
              Your information, including personal data, may be transferred to — and maintained on — computers located
              outside of your state, province, country, or other governmental jurisdiction where data protection laws
              may differ from those in your jurisdiction.
            </p>
            <p className="text-gray-700 mb-4">
              If you are located outside the United States and choose to provide information to us, please note that we
              transfer the data, including personal data, to the United States and process it there.
            </p>
            <p className="text-gray-700 mb-4">
              Your consent to this Privacy Policy followed by your submission of such information represents your
              agreement to that transfer.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-700 mb-4">
              Our Service is not intended for users under the age of 18. We do not knowingly collect personally
              identifiable information from anyone under the age of 18. If you are a parent or guardian and you are
              aware that your child has provided us with personal data, please contact us immediately.
            </p>
            <p className="text-gray-700 mb-4">
              If we become aware that we have collected personal data from children without verification of parental
              consent, we take steps to remove that information from our servers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Data Breach Notification</h2>
            <p className="text-gray-700 mb-4">
              In the event of a data breach that affects your personal information, we will notify affected users within
              72 hours of becoming aware of the breach, as required by GDPR and other applicable data protection laws.
              We will provide information about the nature of the breach, the data affected, and steps we are taking
              to address it.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Legal Basis for Processing (GDPR)</h2>
            <p className="text-gray-700 mb-4">
              If you are from the EEA, our legal basis for collecting and using your personal information depends on
              the data we collect and the specific context:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>Performance of a contract</strong> - Processing necessary to provide the Service you signed up for</li>
              <li><strong>Legitimate interests</strong> - Processing necessary for our legitimate business interests (e.g., security, fraud prevention)</li>
              <li><strong>Your consent</strong> - Where you have given explicit consent for specific processing activities</li>
              <li><strong>Legal obligation</strong> - Where we must comply with legal requirements</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Law Enforcement and Legal Requests</h2>
            <p className="text-gray-700 mb-4">
              We may disclose your personal information if required to do so by law or in response to valid requests
              by public authorities (e.g., a court, law enforcement, or government agency).
            </p>
            <p className="text-gray-700 mb-4">
              <strong>We will never sell your data.</strong> We will only disclose data to law enforcement when legally
              compelled by a valid subpoena, court order, or similar legal process.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new
              Privacy Policy on this page and updating the "Last updated" date at the top.
            </p>
            <p className="text-gray-700 mb-4">
              We will notify you via email and/or a prominent notice on our Service prior to the change becoming
              effective if the changes are material. You are advised to review this Privacy Policy periodically for
              any changes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about this Privacy Policy or wish to exercise your data protection rights,
              please contact us:
            </p>
            <ul className="list-none text-gray-700 space-y-2">
              <li>By email: <a href="mailto:privacy@vistora.app" className="text-blue-600 hover:underline">privacy@vistora.app</a></li>
              <li>For data protection inquiries: <a href="mailto:privacy@vistora.app" className="text-blue-600 hover:underline">privacy@vistora.app</a></li>
              <li>By mail: Vistora, Inc., 1111B S Governors Ave STE 98683, Dover, DE 19904</li>
            </ul>
          </section>

          <section className="mb-8">
            <p className="text-gray-700 mb-4">
              For more information about our terms of service, please visit our{' '}
              <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link> page.
            </p>
          </section>
        </div>
      </div>
    </Layout>
  );
}
